import { useRef, useEffect, useState } from 'react';
import { Button, Descriptions, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
// Note(Anson Kan): might because of SVG doesn't work well with Safari, using `png` images instead, for more details https://github.com/niklasvh/html2canvas/issues/2688
import membershipCardEA from 'assets/images/europ.png';
import membershipCardIP from 'assets/images/medpass.png';
import { useBreakpoint } from 'hooks';
import MedicalCard from './MedicalCard';
import { MedicalCardSectionData } from '../interfaces';
import { CARD_PROVIDER } from '../enums';
import { StyledTitle } from 'components/StyledTitle';
import html2canvas from 'html2canvas';
import { Avatar } from 'components/avatar';
import CryptoJS from 'crypto-js';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { useMedicalCardDownload } from 'all-in-one-shared-api';

const { Text } = Typography;

const Wrapper = styled(Space)`
  width: 100%;
  margin-top: 40px;

  .ant-descriptions-item-content {
    background-color: var(--white);
  }
`;

const CardWrapper = styled.div`
  margin: 10px 0;
  display: flex;
  justify-content: center;
`;

const MedicalCardSection = ({
  name,
  firstName,
  lastName,
  copayments,
  remark,
  avatarUrl,
  serviceProvider,
  generatedOn,
  ...props
}: MedicalCardSectionData): JSX.Element => {
  const { t } = useTranslation();
  const medicalCardRef = useRef<HTMLDivElement | null>(null);
  const { md } = useBreakpoint();

  const { token } = useKeycloakReady();
  const [encryptUrl, setEncryptUrl] = useState('');
  const downloadCardRef = useRef<HTMLDivElement | null>(null);

  const getCardImage = (provider: string) => (provider === CARD_PROVIDER.EA ? membershipCardEA : membershipCardIP);

  const saveMedicalCard = async () => {
    if (!downloadCardRef.current) return;

    const canvas = await html2canvas(downloadCardRef.current);
    const dataUrl = canvas.toDataURL('image/png');

    if (token) {
      const key = sha256(token);
      const encryptedDataUrl = encryptAesDataUrl(
        dataUrl.substring(dataUrl.indexOf(',') + 1),
        key.slice(0, 32),
        key.slice(-16),
      );
      setEncryptUrl(encryptedDataUrl);
      shouldFetch(true);
    }
  };

  const { shouldFetch, data } = useMedicalCardDownload(
    { key: 'useMedicalCardDownload', params: { base64Str: encryptUrl } },
    token ?? '',
    encryptUrl,
  );

  useEffect(() => {
    if (data) {
      shouldFetch(false);
      const link = document.createElement('a');
      link.download = `${name} ${t('membershipCardDetail.medicalCardImageNameSuffix')}.png`;
      const url = URL.createObjectURL(data);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
    }
  });

  const sha256 = (message: string) => {
    return CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex);
  };

  const encryptAesDataUrl = (dataUrl: string, key: string, i?: string) => {
    const keydata = CryptoJS.enc.Utf8.parse(key);
    if (i) {
      const iv = CryptoJS.enc.Utf8.parse(i);
      const encryptedDataUrl = CryptoJS.AES.encrypt(dataUrl, keydata, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }).ciphertext.toString();
      return encryptedDataUrl;
    } else {
      const encryptedDataUrl = CryptoJS.AES.encrypt(dataUrl, keydata, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      }).ciphertext.toString();
      return encryptedDataUrl;
    }
  };

  const _renderMedicalCard = () => (
    <Space direction="vertical">
      {/* 展示用的MedicalCard组件，不显示generatedOn字段 */}
      <MedicalCard
        {...props}
        serviceProvider={getCardImage(serviceProvider)}
        generatedOn=""
        name={name}
        ref={medicalCardRef}
      />

      {/* 隐藏的下载用MedicalCardDownload组件，显示generatedOn字段 */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <MedicalCard
          {...props}
          serviceProvider={getCardImage(serviceProvider)}
          generatedOn={generatedOn}
          name={name}
          ref={downloadCardRef}
        />
      </div>

      <Button icon={<DownloadOutlined />} onClick={() => saveMedicalCard()}>
        {t('membershipCardDetail.saveMedicalCardButton')}
      </Button>
      <Text>{t('membershipCardDetail.saveTips')}</Text>
      <Text>{t('membershipCardDetail.saveSteps')}</Text>
    </Space>
  );

  return (
    <Wrapper direction="vertical">
      <Space size="middle">
        <Avatar size="small" avatarUrl={avatarUrl} />
        <StyledTitle level={5}>{name}</StyledTitle>
      </Space>

      {!md && <CardWrapper>{_renderMedicalCard()}</CardWrapper>}

      <Descriptions bordered column={1}>
        {md && (
          <Descriptions.Item label={t('membershipCardDetail.cardInformation')}>
            {_renderMedicalCard()}
          </Descriptions.Item>
        )}
        <Descriptions.Item
          contentStyle={{ backgroundColor: '#fafafa' }}
          labelStyle={{ borderRight: 'none' }}
          label={t('membershipCardDetail.coPayment')}
        >
          {' '}
        </Descriptions.Item>

        {copayments?.map(({ name, benefitAmount }) => (
          <Descriptions.Item label={name} key={name}>{`$${benefitAmount}`}</Descriptions.Item>
        ))}

        {/* Note(Anson Kan): Use a empty `label` to create a label block */}
        {remark && <Descriptions.Item label=" ">{remark}</Descriptions.Item>}
      </Descriptions>
    </Wrapper>
  );
};

export default MedicalCardSection;
